/**
 * Сборка итога сессии для отправки на сервер.
 *
 * Из доменных объектов формируется компактный агрегат: поток нажатий остаётся
 * на клиенте (решение AD-004, ТЗ §11.4).
 */

import type {
  ErrorKind,
  LanguageCode,
  LayoutId,
  MasteryStat,
  SessionId,
  SyncId,
  TypingError,
  TypingMetrics,
  UnitOutcome,
} from '@type2x/shared-contracts';

import type { SessionPayload, UnitOutcomePayload } from '../contracts/gateway.js';

/** Исходные данные для сборки. */
export interface PayloadInput {
  readonly syncId: SyncId;
  readonly sessionId: SessionId;
  readonly mode: string;
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  /** Календарная метка начала (ISO 8601). */
  readonly startedAt: string;
  /** Длительность по монотонным часам, мс. */
  readonly durationMs: number;
  readonly metrics: TypingMetrics;
  readonly outcomes: readonly UnitOutcome[];
  readonly statsAfter: readonly MasteryStat[];
  readonly errors: readonly TypingError[];
  readonly isControlled: boolean;
}

/**
 * Приставка идентификаторов, которые не покидают устройство.
 *
 * Единица режима «Свой текст» — это слово из текста пользователя, и её
 * идентификатор `custom:договор` сам по себе есть кусок этого текста.
 * Отправлять его нельзя: локально прогресс по такому слову копится, на сервер
 * уходят только метрики сессии (ТЗ §11.2, решение AD-004).
 *
 * Фильтр стоит здесь, а не в режиме: это единственное место, через которое
 * итоги уходят наружу, и новый режим не сможет обойти его по невнимательности.
 */
const PRIVATE_UNIT_PREFIX = 'custom:';

/** Остаётся ли единица на устройстве. */
export function isPrivateUnit(unitId: string): boolean {
  return unitId.startsWith(PRIVATE_UNIT_PREFIX);
}

/** Сводка по типам ошибок сессии. */
export function errorBreakdown(errors: readonly TypingError[]): Record<string, number> {
  const counts: Partial<Record<ErrorKind, number>> = {};
  for (const error of errors) {
    counts[error.kind] = (counts[error.kind] ?? 0) + 1;
  }
  return counts as Record<string, number>;
}

/**
 * Итоговое состояние единицы после занятия.
 *
 * Отправляется состояние целиком, а не наблюдения прогона: сервер его хранит,
 * а не выводит заново. Иначе состояний два, и они расходятся — так и было,
 * пока сервер копил попытки сам, а долю ошибок брал от последнего занятия.
 *
 * Единица без итоговой статистики не отправляется: это единица, по которой в
 * этом занятии ничего не решилось, и посылать о ней нечего.
 */
function toUnitPayload(
  outcome: UnitOutcome,
  statsById: ReadonlyMap<string, MasteryStat>,
): UnitOutcomePayload | null {
  const stat = statsById.get(outcome.unitId);
  if (stat === undefined) return null;

  return {
    unitId: outcome.unitId,
    layoutId: stat.layoutId,
    attempts: stat.attempts,
    firstTryCorrect: stat.firstTryCorrect,
    errorRate: stat.errorRate,
    correctionRate: stat.correctionRate,
    medianLatencyMs: stat.medianLatencyMs,
    p95LatencyMs: stat.p95LatencyMs,
    masteryScore: stat.masteryScore,
    consecutiveSuccesses: stat.consecutiveSuccesses,
    lastSeenAt: stat.lastSeenAt,
    nextReviewAt: stat.nextReviewAt,
  };
}

/** Собирает итог сессии для отправки. */
export function buildSessionPayload(input: PayloadInput): SessionPayload {
  const statsById = new Map(input.statsAfter.map((stat) => [String(stat.unitId), stat]));

  return {
    syncId: input.syncId,
    sessionId: input.sessionId,
    mode: input.mode,
    language: input.language,
    layoutId: input.layoutId,
    startedAt: input.startedAt,
    durationMs: input.durationMs,
    metrics: {
      wpm: input.metrics.wpm,
      cpm: input.metrics.cpm,
      accuracy: input.metrics.accuracy,
      corrections: input.metrics.corrections,
    },
    unitOutcomes: input.outcomes
      .filter((outcome) => !isPrivateUnit(outcome.unitId))
      .map((outcome) => toUnitPayload(outcome, statsById))
      .filter((item): item is UnitOutcomePayload => item !== null),
    errorBreakdown: errorBreakdown(input.errors),
    isControlled: input.isControlled,
  };
}
